/**
 * The Route draft: a request to whoever can act on a gap (#16817, #16812).
 *
 * When the asker cannot close a gap themselves — the data lives with the SIS
 * administrator, HR, or SchoolAI — the Route next step drafts the note for
 * them: who it is for, the question, what is missing, why it matters, and how
 * many other leaders asked. They edit it and COPY it (or open it in their
 * email app). Nothing is sent by the product.
 *
 * ## Plain text from closed vocabulary
 *
 * Every sentence here is a template over closed labels; the only free text is
 * the asker's own question, and the caller passes it through `scrubQueryText`
 * first (`nextStepsForPlan` does). No model prose, so the card renders the
 * draft as the plain text it is.
 *
 * ## Copy rules
 *
 * The data "isn't connected yet" — never "failed", "broke", "error". A gap is
 * a fact about the district's systems, not a malfunction of this one, and a
 * note forwarded to an SIS administrator is the last place to imply a bug.
 *
 * Pure and client-safe: the card builds the clipboard text and the `mailto:`
 * href from the same module the server built the draft with.
 */

import type { RouteRecipientRole } from '@/lib/capability-ledger'

import type { Draft } from './next-steps'

type HandoffDraft = Extract<Draft, { kind: 'handoff' }>

/** How each recipient role reads in the To field and the sentence. */
export const ROUTE_RECIPIENT_LABELS = {
  sis_administrator: 'SIS administrator',
  hr: 'HR',
  schoolai_csm: 'your SchoolAI customer success manager',
  district_administrator: 'district administrator',
  schoolai_support: 'SchoolAI support',
} as const satisfies Record<RouteRecipientRole, string>

/** The window `countOthersAsked` reads, stated where the sentence says it. */
export const OTHERS_ASKED_WINDOW_DAYS = 90

const CLOSING =
  'Could you tell me where this data lives today, or what it would take to ' +
  'connect it?'

const GENERAL_REASON = 'I could answer questions like this from our own data'

function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/**
 * The inline domain labels and intent claims address the LEADER ("your
 * attendance records", "what your policy says", "how you compare"). This
 * note is FROM the leader, so their own district is "our" and "we".
 */
export function fromTheAsker(text: string): string {
  return text.replace(/\byour\b/g, 'our').replace(/\byou\b/g, 'we')
}

/** `a`, `a and b`, `a, b, and c`. */
function joinForProse(items: readonly string[]): string {
  if (items.length <= 2) return items.join(' and ')
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

/**
 * "N other leaders asked", or `null` when nobody else did: zero renders as
 * no sentence, never as "0 others".
 */
export function othersAskedSentence(othersAsked: number): string | null {
  if (othersAsked < 1) return null
  const who = othersAsked === 1 ? 'other leader' : 'other leaders'
  return (
    `${othersAsked} ${who} here asked for the same data in the last ` +
    `${OTHERS_ASKED_WINDOW_DAYS} days.`
  )
}

export interface RouteDraftInput {
  readonly role: RouteRecipientRole
  /** The missing domain, as `INLINE_DOMAIN_LABELS` spells it. */
  readonly wanted: string
  /** What the domain would answer of what was asked, as `INTENT_CLAIMS`. */
  readonly why: readonly string[]
  /** The SCRUBBED question, or `null`. Never the raw text. */
  readonly question: string | null
  readonly othersAsked: number
}

/** The handoff draft for one missing domain. */
export function composeRouteDraft(input: RouteDraftInput): HandoffDraft {
  const wanted = fromTheAsker(input.wanted)
  const question = input.question?.trim() ?? ''
  const reason =
    input.why.length > 0
      ? `I could see ${joinForProse(input.why.map(fromTheAsker))}`
      : GENERAL_REASON
  const parts = [
    question ? `I asked: "${question}"` : null,
    `What's missing: ${wanted}. That data isn't connected yet, so I could ` +
      'not get an answer from it.',
    `Why it matters: with it, ${reason}.`,
    othersAskedSentence(input.othersAsked),
    CLOSING,
  ]
  return {
    kind: 'handoff',
    recipient: capitalizeFirst(ROUTE_RECIPIENT_LABELS[input.role]),
    toRole: input.role,
    subject: `Data request: ${wanted}`,
    body: parts.filter(part => part !== null).join('\n\n'),
    othersAsked: input.othersAsked,
  }
}

const SUPPORT_REQUEST_ROLE: RouteRecipientRole = 'schoolai_support'

const SUPPORT_REQUEST_EXPLANATION =
  "Cadence wasn't sure what to look at for that yet, so it did not answer " +
  'it from our data.'

const SUPPORT_REQUEST_CLOSING =
  'Could you tell me whether Cadence can answer questions like this, and ' +
  'how I should ask it?'

/**
 * The request to SchoolAI support a `NOT_UNDERSTOOD` turn ends with (#16824).
 *
 * No missing domain, so no "what's missing" and no "N others asked" — that
 * count is per domain, and this turn has none. Like every Route draft it is
 * the asker's to edit and copy; nothing is sent. Same copy rule as the
 * module header: a question nobody understood is not a malfunction.
 *
 * @param question - The SCRUBBED question, or `null`. Never the raw text.
 */
export function composeSupportRequestDraft(
  question: string | null,
): HandoffDraft {
  const asked = question?.trim() ?? ''
  const parts = [
    asked ? `I asked: "${asked}"` : null,
    SUPPORT_REQUEST_EXPLANATION,
    SUPPORT_REQUEST_CLOSING,
  ]
  return {
    kind: 'handoff',
    recipient: capitalizeFirst(ROUTE_RECIPIENT_LABELS[SUPPORT_REQUEST_ROLE]),
    toRole: SUPPORT_REQUEST_ROLE,
    subject: 'Question for SchoolAI support',
    body: parts.filter(part => part !== null).join('\n\n'),
    othersAsked: 0,
  }
}

/** What Copy puts on the clipboard: the draft AS EDITED. */
export function handoffClipboardText(draft: HandoffDraft): string {
  const recipient = draft.recipient.trim()
  const to = recipient ? `To: ${recipient}\n` : ''
  return `${to}Subject: ${draft.subject}\n\n${draft.body}`
}

/**
 * Open in email. No address: the recipient is a role or a name, and the
 * asker's email app knows the address better than this product does.
 */
export function handoffMailtoHref(draft: HandoffDraft): string {
  return (
    `mailto:?subject=${encodeURIComponent(draft.subject)}` +
    `&body=${encodeURIComponent(draft.body)}`
  )
}
